import { RowSelectionState } from "@tanstack/react-table";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import React, { useCallback, useMemo } from "react";

interface WithId {
    id: string | number;
}

interface BulkSelectionBarProps<T extends WithId> {
    readonly items: T[] | undefined;
    readonly rowSelection: RowSelectionState | undefined;
    readonly onClearSelection?: () => void;
    readonly onEditItems?: (items: T[]) => void;
    readonly actionLabel?: string;
}

export const BulkSelectionBar = <T extends WithId>({
    items,
    rowSelection,
    onClearSelection,
    onEditItems,
    actionLabel = "Edit",
}: BulkSelectionBarProps<T>): React.ReactNode => {
    const selectedCount = useMemo(
        () =>
            rowSelection
                ? Object.values(rowSelection).filter(Boolean).length
                : 0,
        [rowSelection],
    );

    const handleEditClick = useCallback(() => {
        if (!rowSelection || !items || !onEditItems) {
            return;
        }
        const selectedItems = items.filter((item) => rowSelection[item.id]);
        onEditItems(selectedItems);
    }, [rowSelection, items, onEditItems]);

    if (selectedCount === 0) {
        return null;
    }

    return (
        <>
            <Tooltip content={`Deselect ${selectedCount}`}>
                <Button variant="primary" size="md" onClick={onClearSelection}>
                    {selectedCount} selected
                </Button>
            </Tooltip>

            {onEditItems && (
                <>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleEditClick}
                    >
                        {actionLabel}
                    </Button>
                </>
            )}
        </>
    );
};
